import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Backdrop,
    CircularProgress,
    Card,
    CardContent,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from "@mui/material";
import {
    RefreshSharp,
    Delete,
    Add,
    Visibility,
    Description,
    Edit,
    Info,
    Star,
    Download,
    GroupAdd,
    Send,
} from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

const Templates = () => {
    const navigate = useNavigate();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteDialog, setDeleteDialog] = useState(false);
    const [templateToDelete, setTemplateToDelete] = useState(null); // Will store { id, name }
    const [reviewStatusDialog, setReviewStatusDialog] = useState(false);
    const [qualityRatingDialog, setQualityRatingDialog] = useState(false);
    const [reviewStatusData, setReviewStatusData] = useState(null);
    const [qualityRatingData, setQualityRatingData] = useState(null);
    const [loadingReview, setLoadingReview] = useState(false);

    const showNotification = useNotification();

    // Helper function to extract parameter count from text
    const extractParameterCount = (text) => {
        if (!text) return 0;
        const matches = text.match(/\{\{(\d+)\}\}/g);
        if (!matches) return 0;
        const paramNumbers = matches.map((match) => parseInt(match.replace(/\{\{|\}\}/g, "")));
        return Math.max(...paramNumbers, 0);
    };

    // Function to download Excel for template
    const handleDownloadExcel = async (template) => {
        if (template.status !== "APPROVED") {
            showNotification("Only approved templates can download Excel", "warning");
            return;
        }

        try {
            setLoading(true);
            const templateId = template.template_id || template.id;
            
            // Fetch template details to get components structure
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/template/${templateId}/details`,
                {
                    params: {
                        fields: "id,name,language,category,status,components"
                    }
                }
            );

            const templateDetails = response.data;
            if (!templateDetails || !templateDetails.components) {
                showNotification("Failed to fetch template details", "error");
                return;
            }

            // Extract header and body components
            const headerComponent = templateDetails.components.find((c) => c.type === "HEADER");
            const bodyComponent = templateDetails.components.find((c) => c.type === "BODY");

            // Count parameters (only TEXT format for header)
            let headerParamCount = 0;
            let bodyParamCount = 0;

            if (headerComponent && headerComponent.format === "TEXT" && headerComponent.text) {
                headerParamCount = extractParameterCount(headerComponent.text);
            }

            if (bodyComponent && bodyComponent.text) {
                bodyParamCount = extractParameterCount(bodyComponent.text);
            }

            // Create column headers
            const headers = ["phone_number"];
            
            // Add header parameter columns
            for (let i = 1; i <= headerParamCount; i++) {
                headers.push(`header_param${i}`);
            }

            // Add body parameter columns
            for (let i = 1; i <= bodyParamCount; i++) {
                headers.push(`body_param${i}`);
            }

            // Create empty data row (user will fill this)
            const data = [headers.map(() => "")];

            // Create worksheet
            const ws = XLSX.utils.aoa_to_sheet([headers, ...data]);
            
            // Set column widths
            const colWidths = headers.map(() => ({ wch: 20 }));
            ws["!cols"] = colWidths;

            // Create workbook
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Template Data");

            // Generate filename
            const filename = `${template.name || "template"}_${templateId}.xlsx`;

            // Download file
            XLSX.writeFile(wb, filename);
            showNotification("Excel file downloaded successfully!", "success");
        } catch (error) {
            console.error("Error downloading Excel:", error);
            showNotification(
                error.response?.data?.error || "Failed to download Excel file",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    // Function to navigate to create groups
    const handleCreateGroup = () => {
        navigate("/whatsapp-business/groups");
    };

    // Function to navigate to test template
    const handleTestTemplate = (template) => {
        navigate("/whatsapp-business/test-message", { 
            state: { template: template } 
        });
    };

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            // Backend will automatically use config from database based on authenticated user
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/templates`,
                {
                    params: {
                        save_to_db: true,
                    },
                }
            );

            if (response.data?.data) {
                setTemplates(response.data.data);
                showNotification("Templates fetched successfully!", "success");
            } else {
                setTemplates(response.data || []);
                showNotification("Templates fetched successfully!", "success");
            }
        } catch (error) {
            console.error("Error fetching templates:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch templates",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    useEffect(()=>{
        fetchTemplates();
    },[]);

    const handleCreateTemplate = () => {
        navigate('/whatsapp-business/templates/create');
    };

    const handleEditTemplate = (template) => {
        navigate('/whatsapp-business/templates/edit', { state: { template } });
    };

    const handleDeleteTemplate = async () => {
        if (!templateToDelete || !templateToDelete.id || !templateToDelete.name) return;

        setLoading(true);
        try {
            await axiosInstance.delete(
                `${base_url}/whatsapp-business/template`,
                {
                    params: {
                        hsm_id: templateToDelete.id,
                        name: templateToDelete.name,
                        delete_from_db: true,
                    },
                }
            );

            showNotification("Template deleted successfully!", "success");
            setDeleteDialog(false);
            setTemplateToDelete(null);
            fetchTemplates();
        } catch (error) {
            console.error("Error deleting template:", error);
            showNotification(
                error.response?.data?.error || "Failed to delete template",
                "error"
            );
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case "APPROVED":
                return "success";
            case "PENDING":
                return "warning";
            case "REJECTED":
                return "error";
            default:
                return "default";
        }
    };

    const handleViewReviewStatus = async (templateId) => {
        setLoadingReview(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/template/${templateId}/review-status`
            );
            setReviewStatusData(response.data);
            setReviewStatusDialog(true);
        } catch (error) {
            console.error("Error fetching review status:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch review status",
                "error"
            );
        } finally {
            setLoadingReview(false);
        }
    };

    const handleViewQualityRating = async (templateId) => {
        setLoadingReview(true);
        try {
            const response = await axiosInstance.get(
                `${base_url}/whatsapp-business/template/${templateId}/quality-rating`
            );
            setQualityRatingData(response.data);
            setQualityRatingDialog(true);
        } catch (error) {
            console.error("Error fetching quality rating:", error);
            showNotification(
                error.response?.data?.error || "Failed to fetch quality rating",
                "error"
            );
        } finally {
            setLoadingReview(false);
        }
    };

    return (
        <Box sx={WB_SX.page}>
            <Typography
                variant="h6"
                sx={WB_SX.pageTitle}
            >
                Message Templates
            </Typography>

            <Backdrop
                sx={(theme) => ({
                    color: "#fff",
                    zIndex: theme.zIndex.drawer + 1,
                })}
                open={loading}
            >
                <CircularProgress color="inherit" />
            </Backdrop>

            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <CardContent sx={WB_SX.cardContent}>
                    <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
                        <Button
                            variant="contained"
                            onClick={fetchTemplates}
                            startIcon={<Description />}
                            sx={{ minWidth: "150px", ...WB_SX.primaryButton }}
                        >
                            Fetch Templates
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={fetchTemplates}
                            startIcon={<RefreshSharp />}
                            sx={WB_SX.secondaryButton}
                        >
                            Refresh
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleCreateTemplate}
                            startIcon={<Add />}
                            sx={WB_SX.primaryButton}
                        >
                            Create Template
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            {templates.length > 0 && (
                <TableContainer component={Paper} elevation={0} sx={WB_SX.card}>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: WB_UI.color.tableHeadBg }}>
                                <TableCell sx={WB_SX.tableHeadCell}>Template Name</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Language</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Category</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Status</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell} align="center">Download Excel</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell} align="center">Create Group</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell} align="center">Test Template</TableCell>
                                <TableCell sx={WB_SX.tableHeadCell}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {templates.map((template, index) => (
                                <TableRow key={template.id || index} sx={WB_SX.tableRow}>
                                    <TableCell>{template.name}</TableCell>
                                    <TableCell>{template.language}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={template.category || "—"}
                                            size="small"
                                            sx={WB_SX.categoryChip(template.category)}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={template.status || "UNKNOWN"}
                                            color={getStatusColor(template.status)}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="Download Excel Template">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDownloadExcel(template)}
                                                color="success"
                                                disabled={loading || template.status !== "APPROVED"}
                                            >
                                                <Download />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={template.status !== "APPROVED" ? "Template must be approved to create group" : "Create Group"}>
                                            <IconButton
                                                size="small"
                                                onClick={handleCreateGroup}
                                                color="primary"
                                                disabled={template.status !== "APPROVED"}
                                            >
                                                <GroupAdd />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title={template.status !== "APPROVED" ? "Template must be approved to test" : "Test Template"}>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleTestTemplate(template)}
                                                color="info"
                                                disabled={template.status !== "APPROVED"}
                                            >
                                                <Send />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                                            <Tooltip title="View Details">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const templateId = template.template_id || template.id;
                                                        navigate(`/whatsapp-business/template/${templateId}`);
                                                    }}
                                                    color="primary"
                                                >
                                                    <Visibility />
                                                </IconButton>
                                            </Tooltip>
                                            {/* <Tooltip title="Edit Template">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleEditTemplate(template)}
                                                    color="info"
                                                >
                                                    <Edit />
                                                </IconButton>
                                            </Tooltip> */}
                                            <Tooltip title="Delete">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        setTemplateToDelete({
                                                            id: template.template_id || template.id,
                                                            name: template.name,
                                                        });
                                                        setDeleteDialog(true);
                                                    }}
                                                    color="error"
                                                >
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
                <DialogTitle>Delete Template</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the template "{templateToDelete?.name}"? 
                        This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialog(false)}>Cancel</Button>
                    <Button onClick={handleDeleteTemplate} variant="contained" color="error">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Review Status Dialog */}
            <Dialog 
                open={reviewStatusDialog} 
                onClose={() => {
                    setReviewStatusDialog(false);
                    setReviewStatusData(null);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Info color="info" />
                    Review Status & Reason
                </DialogTitle>
                <DialogContent>
                    {loadingReview ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : reviewStatusData ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Template Status
                                </Typography>
                                <Chip
                                    label={reviewStatusData.status || 'N/A'}
                                    color={
                                        reviewStatusData.status === 'APPROVED' 
                                            ? 'success' 
                                            : reviewStatusData.status === 'PENDING'
                                            ? 'warning'
                                            : reviewStatusData.status === 'REJECTED'
                                            ? 'error'
                                            : 'default'
                                    }
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Rejection Reason
                                </Typography>
                                <Typography variant="body1" sx={{ mt: 0.5 }}>
                                    {reviewStatusData.rejection_reason || 'No rejection reason provided'}
                                </Typography>
                            </Box>
                            {reviewStatusData.name && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Template Name
                                    </Typography>
                                    <Typography variant="body2">
                                        {reviewStatusData.name}
                                    </Typography>
                                </Box>
                            )}
                            {reviewStatusData.id && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Template ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {reviewStatusData.id}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography>No data available</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setReviewStatusDialog(false);
                        setReviewStatusData(null);
                    }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Quality Rating Dialog */}
            <Dialog 
                open={qualityRatingDialog} 
                onClose={() => {
                    setQualityRatingDialog(false);
                    setQualityRatingData(null);
                }}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Star color="warning" />
                    Quality Rating
                </DialogTitle>
                <DialogContent>
                    {loadingReview ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : qualityRatingData ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                            <Box>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Template Status
                                </Typography>
                                <Chip
                                    label={qualityRatingData.status || 'N/A'}
                                    color={
                                        qualityRatingData.status === 'APPROVED' 
                                            ? 'success' 
                                            : qualityRatingData.status === 'PENDING'
                                            ? 'warning'
                                            : qualityRatingData.status === 'REJECTED'
                                            ? 'error'
                                            : 'default'
                                    }
                                    size="small"
                                    sx={{ mt: 0.5 }}
                                />
                            </Box>
                            {qualityRatingData.quality_rating && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Quality Rating
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                        <Chip
                                            label={qualityRatingData.quality_rating}
                                            color={
                                                qualityRatingData.quality_rating === 'GREEN' 
                                                    ? 'success' 
                                                    : qualityRatingData.quality_rating === 'YELLOW'
                                                    ? 'warning'
                                                    : qualityRatingData.quality_rating === 'RED'
                                                    ? 'error'
                                                    : 'default'
                                            }
                                            size="small"
                                        />
                                    </Box>
                                    {qualityRatingData.quality_rating === 'RED' && qualityRatingData.rejection_reason && (
                                        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                                            <strong>Rejection Reason:</strong> {qualityRatingData.rejection_reason}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            {qualityRatingData.name && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Template Name
                                    </Typography>
                                    <Typography variant="body2">
                                        {qualityRatingData.name}
                                    </Typography>
                                </Box>
                            )}
                            {qualityRatingData.id && (
                                <Box>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        Template ID
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {qualityRatingData.id}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    ) : (
                        <Typography>No data available</Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => {
                        setQualityRatingDialog(false);
                        setQualityRatingData(null);
                    }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Templates;

